import React, { useContext } from 'react';
import { type TextFragment } from '@/utils/text-kit';
import { TextWritingType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { useTexture } from '../../hooks';
import { FeatureChecker, getFontFamilySubset, getTextureUrl } from '../../utils';
import { TextLayerContext } from '../context';
import { GlowUse, ShadowUse, StrokeUse } from '../uses';

type Props = {
    layer: ILayerProps;
    listMarkerFragments: TextFragment[];
};

export const CharTypedListMarkerRenderer: React.FC<Props> = ({ layer, listMarkerFragments }) => {
    const { defIDSuffix } = useContext(TextLayerContext)!;

    const layerData = layer.data as ITextLayerProps;
    const { writingMode, fontSize, italic, bold, color, texture } = layerData;
    const fontFamily = getFontFamilySubset(layer);

    const isVertical = writingMode !== TextWritingType.HORIZONTAL_TB;
    const isUsingGradient = FeatureChecker.usingGradient(layer);
    const isUsingTexture = FeatureChecker.usingTexture(layer);
    const fill = (() => {
        if (isUsingGradient) return `url(#gradient-${layer.id}-${defIDSuffix})`;
        if (isUsingTexture) return `url(#texture-${layer.id}-${defIDSuffix})`;
        return color;
    })();

    const { textureConfig } = useTexture(texture);

    return (
        <g data-role="list-marker">
            {listMarkerFragments.map((item, idx) => {
                const id = `list-marker-char-typed-${layer.id}-${item.listOrder}`;
                const x = document.documentElement.scrollLeft + (isVertical ? item.left + item.width : item.left);
                const y = document.documentElement.scrollTop + item.top;

                const textElement = (
                    <text
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                        id={id}
                        x={x}
                        y={y}
                        letterSpacing={0}
                        writingMode={writingMode}
                        dominantBaseline="text-before-edge"
                        fontFamily={fontFamily}
                        fontSize={fontSize}
                        textDecoration="none"
                        fontStyle={italic ? 'italic' : 'normal'}
                        fontWeight={bold ? 'bold' : 'normal'}
                    >
                        {item.text}
                    </text>
                );

                if (isVertical && isUsingTexture) {
                    const { width, height } = item;
                    const x = document.documentElement.scrollLeft + item.left;
                    const y = document.documentElement.scrollTop + item.top;
                    const textureImageWidth =
                        textureConfig?.stretch === 'repeat' ? textureConfig?.width || width : width;
                    const textureImageHeight =
                        textureConfig?.stretch === 'repeat' ? textureConfig?.height || height : height;
                    const preserveAspectRatio = textureConfig?.stretch === 'repeat' ? 'none' : 'xMidYMid slice';
                    const clipPathId = `clip-${layer.id}-${idx}-list-marker`;

                    return (
                        <React.Fragment key={id}>
                            <defs>
                                {textElement}
                                {<clipPath id={clipPathId}>{textElement}</clipPath>}
                            </defs>

                            {/* 应用外发光效果 */}
                            <GlowUse href={`#${id}`} layer={layer} />

                            {/* 应用阴影特效 */}
                            <ShadowUse href={`#${id}`} layer={layer} />

                            {/* 应用描边效果 */}
                            <StrokeUse href={`#${id}`} layer={layer} />

                            <image
                                data-texture-id={`texture-image-vertical-${layer.id}`}
                                href={getTextureUrl(texture!.url)}
                                width={textureImageWidth}
                                height={textureImageHeight}
                                x={x}
                                y={y}
                                preserveAspectRatio={preserveAspectRatio}
                                clipPath={`url(#${clipPathId})`}
                            />
                        </React.Fragment>
                    );
                }

                if (isVertical && isUsingGradient) {
                    const { width, height } = item;
                    const x = document.documentElement.scrollLeft + item.left;
                    const y = document.documentElement.scrollTop + item.top;
                    const fill = `url(#gradient-${layer.id})`;
                    const clipPathId = `clip-${layer.id}-${idx}-list-marker`;

                    return (
                        <React.Fragment key={id}>
                            <defs>
                                {textElement}
                                {<clipPath id={clipPathId}>{textElement}</clipPath>}
                            </defs>

                            {/* 应用外发光效果 */}
                            <GlowUse href={`#${id}`} layer={layer} />

                            {/* 应用阴影特效 */}
                            <ShadowUse href={`#${id}`} layer={layer} />

                            {/* 应用描边效果 */}
                            <StrokeUse href={`#${id}`} layer={layer} />

                            <rect
                                x={x}
                                y={y}
                                width={width}
                                height={height}
                                fill={fill}
                                clipPath={`url(#${clipPathId})`}
                            />
                        </React.Fragment>
                    );
                }

                return (
                    <React.Fragment key={id}>
                        <defs>{textElement}</defs>

                        {/* 应用外发光效果 */}
                        <GlowUse href={`#${id}`} layer={layer} />

                        {/* 应用阴影特效 */}
                        <ShadowUse href={`#${id}`} layer={layer} />

                        {/* 应用描边效果 */}
                        <StrokeUse href={`#${id}`} layer={layer} />

                        <use xlinkHref={`#${id}`} fill={fill} />
                    </React.Fragment>
                );
            })}
        </g>
    );
};
